import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Space, Typography, DatePicker, Modal, message, Tag } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { fetchSqlData, updateData } from '../../../services/api';
import { useGetVwConstMachines, useGetPersonnel } from '../../../hooks/genericHooks';
import { useAuth } from '../../../components/common/security/AuthContext';
import type { ConstMachineAllocation, VwConstMachines, VwPersonnel } from '../../../types';
import Button from '../../../components/common/Button';

const { Title } = Typography;

const MachMachineAllocationView: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { personnelId } = useAuth();

  const [record, setRecord] = React.useState<ConstMachineAllocation | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [deferModalOpen, setDeferModalOpen] = React.useState(false);
  const [deferStart, setDeferStart] = React.useState<Dayjs | null>(null);
  const [deferEnd, setDeferEnd] = React.useState<Dayjs | null>(null);

  const { data: machinesData } = useGetVwConstMachines();
  const { data: personnelData } = useGetPersonnel();

  const machines: VwConstMachines[] = Array.isArray(machinesData)
    ? (machinesData as any)
    : (machinesData?.data || []);
  const persons: VwPersonnel[] = Array.isArray(personnelData)
    ? (personnelData as any)
    : (personnelData?.data || []);

  const machineMap = React.useMemo(() => {
    const m = new Map<number, string>();
    (machines || []).forEach((x) => {
      const label =
        (x.license_plate || '').trim() ||
        `${x.make || ''} ${x.model || ''}`.trim() ||
        `ID ${x.id}`;
      m.set(x.id, label);
    });
    return m;
  }, [machines]);

  const personMap = React.useMemo(() => {
    const m = new Map<number, string>();
    (persons || []).forEach((p) => {
      const full = `${p.first_name || ''} ${p.last_name || ''}`.trim();
      m.set(p.id, `${full || t('machinery.allocations.unknown')} (${p.pers_id})`);
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persons, t]);

  const loadRecord = React.useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetchSqlData('const_machine_allocation', 'id', Number(id));
      const arr = res && res.data ? res.data : Array.isArray(res) ? res : [];
      if (Array.isArray(arr) && arr.length > 0) {
        setRecord(arr[0] as ConstMachineAllocation);
        const rec = arr[0] as ConstMachineAllocation;
        setDeferStart(rec.start_time ? dayjs(rec.start_time) : null);
        setDeferEnd(rec.end_time ? dayjs(rec.end_time) : null);
      } else {
        message.error(t('machinery.allocations.recordNotFound'));
      }
    } catch (e: any) {
      console.error(e);
      message.error(e?.message || t('machinery.allocations.recordLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    void loadRecord();
  }, [loadRecord]);

  const currentPersonnelId = React.useMemo(
    () => (typeof window !== 'undefined' ? localStorage.getItem('personnel_id') || personnelId : personnelId),
    [personnelId]
  );

  const isCurrentUserApprover = React.useMemo(() => {
    if (!record?.approver_personnel_id) return false;
    if (!currentPersonnelId) return false;
    return String(record.approver_personnel_id) === String(currentPersonnelId);
  }, [record, currentPersonnelId]);

  const applyStatusChange = async (
    nextStatus: ConstMachineAllocation['status'],
    extra: Partial<ConstMachineAllocation> = {}
  ) => {
    if (!record?.id) return;
    // Only the assigned approver is allowed to change status
    if (!isCurrentUserApprover) {
      message.error(t('machinery.allocations.onlyApprover'));
      return;
    }
    try {
      setLoading(true);
      await updateData('const_machine_allocation', Number(record.id), {
        status: nextStatus,
        ...extra,
      });
      message.success(t('machinery.allocations.statusUpdated', { status: nextStatus }));
      try {
        window.dispatchEvent(new CustomEvent('const_machine_allocation:changed'));
      } catch {}
      await loadRecord();
    } catch (e: any) {
      console.error(e);
      message.error(e?.message || t('machinery.allocations.statusUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = () => {
    applyStatusChange('approved');
  };

  const handleCancel = () => {
    Modal.confirm({
      title: t('machinery.allocations.cancelConfirm'),
      okText: t('machinery.allocations.cancelOk'),
      cancelText: t('machinery.allocations.no'),
      onOk: () => applyStatusChange('cancelled'),
    });
  };

  const handleReject = () => {
    Modal.confirm({
      title: t('machinery.allocations.rejectConfirm'),
      okText: t('machinery.allocations.rejectOk'),
      cancelText: t('machinery.allocations.no'),
      onOk: () => applyStatusChange('rejected'),
    });
  };

  const handleDefer = () => {
    if (!record) return;
    setDeferStart(record.start_time ? dayjs(record.start_time) : dayjs());
    setDeferEnd(record.end_time ? dayjs(record.end_time) : dayjs().add(4, 'hour'));
    setDeferModalOpen(true);
  };

  const handleDeferConfirm = async () => {
    if (!deferStart || !deferEnd) {
      message.warning(t('machinery.allocations.selectNewTimes'));
      return;
    }
    if (deferEnd.isSameOrBefore(deferStart)) {
      message.warning(t('machinery.allocations.endAfterStart'));
      return;
    }
    await applyStatusChange('deferred', {
      start_time: deferStart.toISOString(),
      end_time: deferEnd.toISOString(),
    } as any);
    setDeferModalOpen(false);
  };

  const statusTag = (status: ConstMachineAllocation['status'] | undefined) => {
    if (!status) return null;
    let color: string = 'default';
    if (status === 'requested') color = 'blue';
    else if (status === 'approved') color = 'green';
    else if (status === 'deferred') color = 'orange';
    else if (status === 'rejected') color = 'red';
    else if (status === 'cancelled') color = 'volcano';
    else if (status === 'completed') color = 'geekblue';
    return <Tag color={color}>{String(status).toUpperCase()}</Tag>;
  };

  return (
    <Card style={{ margin: 16 }} loading={loading}>
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            {t('machinery.allocations.viewTitle')}
          </Title>
          <Space>
            {statusTag(record?.status as any)}
            <Button onClick={() => navigate('/machinery/allocations')}>{t('machinery.allocations.backToList')}</Button>
            <Button
              action="edit"
              onClick={handleApprove}
              type="primary"
              disabled={!record || record.status === 'approved' || !isCurrentUserApprover}
            >
              {t('machinery.allocations.approve')}
            </Button>
            <Button action="edit" onClick={handleDefer} disabled={!record || !isCurrentUserApprover}>
              {t('machinery.allocations.defer')}
            </Button>
            <Button action="edit" danger onClick={handleCancel} disabled={!record || !isCurrentUserApprover}>
              {t('machinery.allocations.cancel')}
            </Button>
            <Button action="edit" danger onClick={handleReject} disabled={!record || !isCurrentUserApprover}>
              {t('machinery.allocations.reject')}
            </Button>
          </Space>
        </Space>

        {record && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Ref #">{record.ref_num}</Descriptions.Item>
            <Descriptions.Item label={t('machinery.allocations.machine')}>
              {machineMap.get(record.machine_id) || record.machine_id}
            </Descriptions.Item>
            <Descriptions.Item label={t('machinery.allocations.requester')}>
              {personMap.get(record.requester_personnel_id) || record.requester_personnel_id}
            </Descriptions.Item>
            <Descriptions.Item label={t('machinery.allocations.approver')}>
              {record.approver_personnel_id
                ? (personMap.get(record.approver_personnel_id) || record.approver_personnel_id)
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('machinery.allocations.location')}>{record.location}</Descriptions.Item>
            <Descriptions.Item label={t('machinery.allocations.description')}>{record.description}</Descriptions.Item>
            <Descriptions.Item label={t('machinery.allocations.startTime')}>
              {record.start_time}
            </Descriptions.Item>
            <Descriptions.Item label={t('machinery.allocations.endTime')}>
              {record.end_time}
            </Descriptions.Item>
            <Descriptions.Item label={t('machinery.allocations.workPermitRef')}>
              {record.workpermit_ref_num || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('machinery.allocations.activity')}>
              {record.activity_code || record.activity_name
                ? `${record.activity_code || ''} - ${record.activity_name || ''}`.trim()
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Space>

      <Modal
        open={deferModalOpen}
        title={t('machinery.allocations.deferTitle')}
        onCancel={() => setDeferModalOpen(false)}
        onOk={handleDeferConfirm}
        okText={t('machinery.allocations.save')}
        cancelText={t('machinery.allocations.cancel')}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <DatePicker
            showTime
            style={{ width: '100%' }}
            value={deferStart}
            onChange={(v) => setDeferStart(v)}
            placeholder={t('machinery.allocations.newStart')}
          />
          <DatePicker
            showTime
            style={{ width: '100%' }}
            value={deferEnd}
            onChange={(v) => setDeferEnd(v)}
            placeholder={t('machinery.allocations.newEnd')}
          />
        </Space>
      </Modal>
    </Card>
  );
};

export default MachMachineAllocationView;

